import { apiClient } from '@/lib/api/client';
import {
  ProjectSchema,
  ProjectListSchema,
  PaginatedProjectResponseSchema,
  CreateProjectSchema,
  UpdateProjectSchema,
  ProjectQueryParamsSchema,
  ProjectWithStatisticsSchema,
  ProjectStatisticsSchema,
  type Project,
  type ProjectList,
  type PaginatedProjectResponse,
  type CreateProject,
  type UpdateProject,
  type ProjectQueryParams,
  type ProjectWithStatistics,
  type ProjectStatistics,
} from './projects.schemas';

// Re-export types for use in hooks and components
export type {
  Project,
  ProjectList,
  PaginatedProjectResponse,
  CreateProject,
  UpdateProject,
  ProjectQueryParams,
  ProjectWithStatistics,
  ProjectStatistics,
};

// ============================================================================
// Response Normalization
// ============================================================================

/**
 * The BFF upstream (organization-manager-api) returns projects in a camelCase
 * shape without `tenant_id` / `organization_id` and with `enabled` instead of
 * `status`. This normalizes the payload to the shape expected by ProjectSchema.
 */
function normalizeProject(raw: any, organizationId: string) {
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description || undefined,
    created_at: raw.createdAt || raw.created_at,
    updated_at: raw.updatedAt || raw.updated_at,
    tenant_id: raw.tenant_id || organizationId,
    organization_id: raw.organization_id || organizationId,
    settings: raw.settings || undefined,
    status:
      raw.status ??
      (typeof raw.enabled === 'boolean'
        ? raw.enabled ? 'active' : 'inactive'
        : 'active'),
    metadata: raw.metadata || undefined,
  };
}

// ============================================================================
// Project CRUD API Functions
// ============================================================================

/**
 * Fetch all projects for the current tenant
 * @param params - Optional query parameters for filtering and pagination
 * @returns Promise<PaginatedProjectResponse> - Paginated list of projects
 */
export async function fetchProjects(
  params?: ProjectQueryParams
): Promise<PaginatedProjectResponse> {
  // Get user session and selected organization
  const { useAuthStore } = await import('@/store/authStore');
  const { useSettingsStore } = await import('@/store/settingsStore');
  const session = useAuthStore.getState().session;
  const { selectedOrganization } = useSettingsStore.getState();
  
  if (!session?.identity?.metadata_public?.tenants) {
    throw new Error('No tenant information available in session');
  }

  if (!selectedOrganization) {
    throw new Error('No organization selected');
  }

  // Verify the selected organization exists in session
  const tenant = session.identity.metadata_public.tenants.find(
    t => t.id === selectedOrganization.id
  );
  
  if (!tenant) {
    throw new Error('Selected organization not found in session');
  }

  const organizationId = selectedOrganization.id;

  // Validate input parameters with Zod
  const validatedParams = params ? ProjectQueryParamsSchema.parse(params) : {
    page: 1,
    per_page: 20,
    sort_by: 'created_at' as const,
    sort_order: 'desc' as const,
  };

  // Make API request
  const response = await apiClient.get(`/organizations/${organizationId}/projects`, {
    params: validatedParams,
  });

  // Handle different response formats
  const responseData = response.data;
  
  // Check if response has the new format with success and projects
  if (responseData.success && responseData.projects) {
    const transformedProjects = responseData.projects.map((project: any) =>
      normalizeProject(project, organizationId),
    );

    const transformedResponse = {
      data: transformedProjects,
      pagination: {
        page: validatedParams.page,
        per_page: validatedParams.per_page,
        total: transformedProjects.length,
        total_pages: 1,
      },
    };
    return PaginatedProjectResponseSchema.parse(transformedResponse);
  }

  // Validate response with Zod (original format)
  return PaginatedProjectResponseSchema.parse(responseData);
}

/**
 * Fetch a single project by ID
 * @param projectId - The project ID
 * @returns Promise<Project> - The project data
 */
export async function fetchProject(projectId: string): Promise<Project> {
  // Get user session and selected organization
  const { useAuthStore } = await import('@/store/authStore');
  const { useSettingsStore } = await import('@/store/settingsStore');
  const session = useAuthStore.getState().session;
  const { selectedOrganization } = useSettingsStore.getState();
  
  if (!session?.identity?.metadata_public?.tenants) {
    throw new Error('No tenant information available in session');
  }

  if (!selectedOrganization) {
    throw new Error('No organization selected');
  }

  // Verify the selected organization exists in session
  const tenant = session.identity.metadata_public.tenants.find(
    t => t.id === selectedOrganization.id
  );
  
  if (!tenant) {
    throw new Error('Selected organization not found in session');
  }

  const organizationId = selectedOrganization.id;

  // Make API request
  const response = await apiClient.get(`/organizations/${organizationId}/projects/${projectId}`);

  // Normalize + validate response with Zod
  return ProjectSchema.parse(normalizeProject(response.data, organizationId));
}

/**
 * Create a new project
 * @param projectData - The project data to create
 * @returns Promise<Project> - The created project
 */
export async function createProject(projectData: CreateProject): Promise<Project> {
  // Get user session and selected organization
  const { useAuthStore } = await import('@/store/authStore');
  const { useSettingsStore } = await import('@/store/settingsStore');
  const session = useAuthStore.getState().session;
  const { selectedOrganization } = useSettingsStore.getState();
  
  if (!session?.identity?.metadata_public?.tenants) {
    throw new Error('No tenant information available in session');
  }

  if (!selectedOrganization) {
    throw new Error('No organization selected');
  }

  // Verify the selected organization exists in session
  const tenant = session.identity.metadata_public.tenants.find(
    t => t.id === selectedOrganization.id
  );
  
  if (!tenant) {
    throw new Error('Selected organization not found in session');
  }

  const organizationId = selectedOrganization.id;

  // Validate input with Zod
  const validatedData = CreateProjectSchema.parse(projectData);

  // Make API request
  const response = await apiClient.post(`/organizations/${organizationId}/projects`, validatedData);

  // Normalize + validate response with Zod
  return ProjectSchema.parse(normalizeProject(response.data, organizationId));
}

/**
 * Update an existing project
 * @param projectId - The project ID to update
 * @param projectData - The project data to update
 * @returns Promise<Project> - The updated project
 */
export async function updateProject(
  projectId: string,
  projectData: UpdateProject
): Promise<Project> {
  // Get user session and selected organization
  const { useAuthStore } = await import('@/store/authStore');
  const { useSettingsStore } = await import('@/store/settingsStore');
  const session = useAuthStore.getState().session;
  const { selectedOrganization } = useSettingsStore.getState();
  
  if (!session?.identity?.metadata_public?.tenants) {
    throw new Error('No tenant information available in session');
  }

  if (!selectedOrganization) {
    throw new Error('No organization selected');
  }

  // Verify the selected organization exists in session
  const tenant = session.identity.metadata_public.tenants.find(
    t => t.id === selectedOrganization.id
  );
  
  if (!tenant) {
    throw new Error('Selected organization not found in session');
  }

  const organizationId = selectedOrganization.id;

  // Validate input with Zod
  const validatedData = UpdateProjectSchema.parse(projectData);

  // Make API request
  const response = await apiClient.patch(`/organizations/${organizationId}/projects/${projectId}`, validatedData);

  // Normalize + validate response with Zod
  return ProjectSchema.parse(normalizeProject(response.data, organizationId));
}

/**
 * Delete a project
 * @param projectId - The project ID to delete
 * @returns Promise<void> - Resolves when deletion is complete
 */
export async function deleteProject(projectId: string): Promise<void> {
  // Get user session and selected organization
  const { useAuthStore } = await import('@/store/authStore');
  const { useSettingsStore } = await import('@/store/settingsStore');
  const session = useAuthStore.getState().session;
  const { selectedOrganization } = useSettingsStore.getState();
  
  if (!session?.identity?.metadata_public?.tenants) {
    throw new Error('No tenant information available in session');
  }

  if (!selectedOrganization) {
    throw new Error('No organization selected');
  }

  // Verify the selected organization exists in session
  const tenant = session.identity.metadata_public.tenants.find(
    t => t.id === selectedOrganization.id
  );
  
  if (!tenant) {
    throw new Error('Selected organization not found in session');
  }

  const organizationId = selectedOrganization.id;

  // Make API request
  await apiClient.delete(`/organizations/${organizationId}/projects/${projectId}`);
}

// ============================================================================
// Project Statistics API Functions
// ============================================================================

/**
 * Fetch project statistics
 * @param projectId - The project ID
 * @returns Promise<ProjectStatistics> - The project statistics
 */
export async function fetchProjectStatistics(projectId: string): Promise<ProjectStatistics> {
  // Get user session and selected organization
  const { useAuthStore } = await import('@/store/authStore');
  const { useSettingsStore } = await import('@/store/settingsStore');
  const session = useAuthStore.getState().session;
  const { selectedOrganization } = useSettingsStore.getState();
  
  if (!session?.identity?.metadata_public?.tenants) {
    throw new Error('No tenant information available in session');
  }

  if (!selectedOrganization) {
    throw new Error('No organization selected');
  }

  // Verify the selected organization exists in session
  const tenant = session.identity.metadata_public.tenants.find(
    t => t.id === selectedOrganization.id
  );
  
  if (!tenant) {
    throw new Error('Selected organization not found in session');
  }

  const organizationId = selectedOrganization.id;

  // Make API request
  const response = await apiClient.get(`/organizations/${organizationId}/projects/${projectId}/statistics`);

  // Validate response with Zod
  return ProjectStatisticsSchema.parse(response.data);
}

/**
 * Fetch project with statistics
 * @param projectId - The project ID
 * @returns Promise<ProjectWithStatistics> - The project with statistics
 */
export async function fetchProjectWithStatistics(projectId: string): Promise<ProjectWithStatistics> {
  // Get user session and selected organization
  const { useAuthStore } = await import('@/store/authStore');
  const { useSettingsStore } = await import('@/store/settingsStore');
  const session = useAuthStore.getState().session;
  const { selectedOrganization } = useSettingsStore.getState();
  
  if (!session?.identity?.metadata_public?.tenants) {
    throw new Error('No tenant information available in session');
  }

  if (!selectedOrganization) {
    throw new Error('No organization selected');
  }

  // Verify the selected organization exists in session
  const tenant = session.identity.metadata_public.tenants.find(
    t => t.id === selectedOrganization.id
  );
  
  if (!tenant) {
    throw new Error('Selected organization not found in session');
  }

  const organizationId = selectedOrganization.id;

  // Make API request
  const response = await apiClient.get(`/organizations/${organizationId}/projects/${projectId}?include_statistics=true`);

  // Validate response with Zod
  return ProjectWithStatisticsSchema.parse(response.data);
}

// ============================================================================
// Project Settings API Functions
// ============================================================================

/**
 * Update project settings
 * @param projectId - The project ID
 * @param settings - The settings to update
 * @returns Promise<Project> - The updated project
 */
export async function updateProjectSettings(
  projectId: string,
  settings: Partial<Project['settings']>
): Promise<Project> {
  return updateProject(projectId, { settings });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get default project query parameters
 * @returns ProjectQueryParams - Default parameters
 */
export function getDefaultProjectParams(): ProjectQueryParams {
  return {
    page: 1,
    per_page: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
  };
}

/**
 * Validate project query parameters
 * @param params - Parameters to validate
 * @returns ProjectQueryParams - Validated parameters
 */
export function validateProjectParams(params: unknown): ProjectQueryParams {
  return ProjectQueryParamsSchema.parse(params);
}

/**
 * Check if user has access to project
 * @param projectId - The project ID to check
 * @returns Promise<boolean> - True if user has access
 */
export async function checkProjectAccess(projectId: string): Promise<boolean> {
  try {
    await fetchProject(projectId);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Search projects by name
 * @param searchTerm - The search term
 * @param params - Optional additional query parameters
 * @returns Promise<PaginatedProjectResponse> - Filtered projects
 */
export async function searchProjects(
  searchTerm: string,
  params?: Omit<ProjectQueryParams, 'search'>
): Promise<PaginatedProjectResponse> {
  const searchParams: ProjectQueryParams = {
    ...getDefaultProjectParams(),
    ...params,
    search: searchTerm,
  };

  return fetchProjects(searchParams);
}

/**
 * Get projects by status
 * @param status - The project status to filter by
 * @param params - Optional additional query parameters
 * @returns Promise<PaginatedProjectResponse> - Filtered projects
 */
export async function getProjectsByStatus(
  status: 'active' | 'inactive' | 'suspended',
  params?: Omit<ProjectQueryParams, 'status'>
): Promise<PaginatedProjectResponse> {
  const statusParams: ProjectQueryParams = {
    ...getDefaultProjectParams(),
    ...params,
    status,
  };

  return fetchProjects(statusParams);
}